/**
 * Owner: Masa Bayu
 * File: admin-view.ts
 * Date:Time: 2026-10-23 09:30
 * Description: §467-D — model paparan papan pemuka pentadbir. Semua nilai
 *   dibentuk DARIPADA respons hub (`GET /v1/admin/dashboard`, `GET /v1/admin/orders`)
 *   atau `null`/en-dash bila sumber tiada. Tiada angka dikira di klien yang
 *   boleh bercanggah dengan pelayan: hasil KPI ialah kiraan hub, dan hasil
 *   rentas mata wang TIDAK pernah dijumlah (doktrin perakaunan §467-B).
 */

import {
  formatMarketplaceAmount,
  formatMarketplacePriceOrUnknown,
  type MarketplaceLanguage,
} from '../home/marketplace/marketplace-locale';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView, HubAdminBook, HubMoney } from './admin-dashboard';

/** Nilai yang tiada sumber di pelayan — dilukis sebagai en-dash, bukan 0. */
export const NO_DATA = '—';

/** Harga: pemformatan mata wang ialah SSOT `marketplace-locale` (§466 S1). Kod yang
 *  tidak dikenali tetap dipapar dengan koma ribuan + kod, bukan symbol rekaan. */
export function moneyText(
  minorUnits: number | null,
  currency: string | null,
  unknownLabel: string,
): string {
  if (
    minorUnits === null ||
    !Number.isInteger(minorUnits) ||
    typeof currency !== 'string' ||
    currency.length === 0
  ) {
    return unknownLabel;
  }
  const formatted = formatMarketplacePriceOrUnknown(minorUnits, currency, '');
  return formatted.length > 0 ? formatted : `${formatMarketplaceAmount(minorUnits)} ${currency}`;
}

export function moneyOf(row: HubMoney, unknownLabel: string): string {
  return moneyText(row.minorUnits, row.currency, unknownLabel);
}

/** Jumlah rentas mata wang dilarang; paparkan satu baris per kod. */
export function moneyLines(rows: readonly HubMoney[], unknownLabel: string): readonly string[] {
  return rows.map((row) => `${moneyOf(row, unknownLabel)} · ${String(row.orders)}`);
}

export function revenueText(rows: readonly HubMoney[], la: AdminLabels): string {
  const lines = moneyLines(rows, la.noData);
  return lines.length === 0 ? NO_DATA : lines.join('  |  ');
}

const MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTHS_MS = [
  'Jan',
  'Feb',
  'Mac',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Ogo',
  'Sep',
  'Okt',
  'Nov',
  'Dis',
];

/** 'YYYY-MM' → label pendek ikut bahasa UI; bentuk lain dipulang mentah. */
export function monthLabel(month: string, language: MarketplaceLanguage): string {
  const matched = /^(\d{4})-(\d{2})$/.exec(month);
  if (matched === null) return month;
  const [, year, mm] = matched;
  if (year === undefined || mm === undefined) return month;
  const table = language === 'ms' ? MONTHS_MS : MONTHS_EN;
  const name = table[Number(mm) - 1] ?? mm;
  return `${name} ${year.slice(2)}`;
}

/** Tarikh ISO → 'YYYY-MM-DD'; null → en-dash (tiada "undefined" di skrin). */
export function dateText(iso: string | null): string {
  return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : NO_DATA;
}

export function dateTimeText(iso: string | null): string {
  if (typeof iso !== 'string' || iso.length < 16) return NO_DATA;
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

export interface AdminKpi {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly sub?: string;
}

/** KPI ringkasan — semua daripada `catalog`/`counts` hub, tiada kiraan klien. */
export function overviewKpis(view: AdminDashboardView, la: AdminLabels): readonly AdminKpi[] {
  const pending = view.counts.orders.byState.pending ?? 0;
  const paid = view.counts.orders.byState.paid ?? 0;
  return [
    { id: 'publications', label: la.kpiPublications, value: String(view.catalog.publications) },
    {
      id: 'priced',
      label: la.kpiPriced,
      value: `${String(view.catalog.pricedPublications)}/${String(view.catalog.publications)}`,
    },
    {
      id: 'accounts',
      label: la.kpiAccounts,
      value: String(view.counts.accounts.total),
      sub: `${la.roleReader} ${String(view.counts.accounts.reader)} · ${la.rolePublisher} ${String(
        view.counts.accounts.publisher,
      )} · ${la.roleAdmin} ${String(view.counts.accounts.admin)}`,
    },
    { id: 'paid', label: la.kpiPaidOrders, value: String(paid) },
    { id: 'pending', label: la.kpiPendingOrders, value: String(pending) },
    { id: 'grants', label: la.kpiActiveGrants, value: String(view.counts.activeGrants) },
    {
      id: 'sessions',
      label: la.kpiSessions,
      value: `${String(view.counts.sessions.active)}/${String(view.counts.sessions.total)}`,
    },
    { id: 'artifacts', label: la.kpiArtifacts, value: String(view.counts.artifactDirs) },
    { id: 'takedowns', label: la.kpiTakedowns, value: String(view.counts.takedownDirs) },
  ];
}

export interface AdminSeriesPoint {
  readonly month: string;
  readonly label: string;
  readonly orders: number;
  readonly rows: readonly string[];
}

/** Siri bulanan: satu titik per bulan, nilai per mata wang (tiada jumlah rentas). */
export function monthlySeries(
  view: AdminDashboardView,
  language: MarketplaceLanguage,
  unknownLabel: string,
): readonly AdminSeriesPoint[] {
  return view.monthly.map((point) => ({
    month: point.month,
    label: monthLabel(point.month, language),
    orders: point.orders,
    rows: moneyLines(point.revenueByCurrency, unknownLabel),
  }));
}

/** Tinggi bar dinormalkan kepada bulan paling sibuk. Carta memaparkan BILANGAN
 *  pesanan, bukan hasil: hasil tidak boleh dijumlah rentas mata wang, jadi ia
 *  hanya ada sebagai teks per kod (tiada satu angka gabungan yang dikarang). */
export function seriesMax(points: readonly AdminSeriesPoint[]): number {
  return points.reduce((max, point) => Math.max(max, point.orders), 0);
}

export function seriesRatio(orders: number, max: number): number {
  if (!(max > 0)) return 0;
  return Math.max(0, Math.min(1, orders / max));
}

export interface AdminBookRow extends HubAdminBook {
  readonly priceText: string;
  readonly revenueText: string;
  readonly ownerText: string;
  readonly publishedText: string;
  readonly categoryText: string;
}

export function bookRow(book: HubAdminBook, la: AdminLabels): AdminBookRow {
  return {
    ...book,
    priceText: moneyText(book.priceMinorUnits, book.currency, la.unpricedBadge),
    revenueText: revenueText(book.sales.revenueByCurrency, la),
    ownerText: book.ownerEmail ?? book.ownerAccountId ?? la.unownedBadge,
    publishedText: dateText(book.publishedAt),
    categoryText:
      book.category === null
        ? NO_DATA
        : `${book.category}${book.subcategory === null ? '' : ` › ${book.subcategory}`}`,
  };
}

/** Label + warna pill keadaan order; keadaan tidak diketahui tidak dikarang. */
export function stateLabel(state: string | null, la: AdminLabels): string {
  if (state === 'paid') return la.statePaid;
  if (state === 'pending') return la.statePending;
  if (state === 'failed') return la.stateFailed;
  if (state === 'refunded') return la.stateRefunded;
  return state === null ? la.stateUnknown : state;
}

export function stateTone(state: string | null): 'ok' | 'warn' | 'neutral' {
  if (state === 'paid') return 'ok';
  if (state === 'pending') return 'warn';
  return 'neutral';
}

/** Peranan stor → label UI; peranan baharu (belum diterjemah) dipapar mentah. */
export function roleLabel(role: string, la: AdminLabels): string {
  if (role === 'reader') return la.roleReader;
  if (role === 'publisher') return la.rolePublisher;
  if (role === 'admin') return la.roleAdmin;
  return role.length === 0 ? la.stateUnknown : role;
}

/** Cara log masuk akaun — dua-dua kredential mungkin wujud. */
export function signInLabel(
  account: { readonly hasPassword: boolean; readonly googleLinked: boolean },
  la: AdminLabels,
): string {
  const ways: string[] = [];
  if (account.hasPassword) ways.push(la.signInPassword);
  if (account.googleLinked) ways.push(la.signInGoogle);
  return ways.length === 0 ? la.signInNone : ways.join(' + ');
}

/** Julat paparan untuk "Menunjukkan X – Y daripada Z". */
export function pageRange(page: {
  readonly total: number;
  readonly offset: number;
  readonly returned: number;
}): { readonly from: number; readonly to: number } {
  if (page.total === 0 || page.returned === 0) return { from: 0, to: 0 };
  return { from: page.offset + 1, to: page.offset + page.returned };
}

export const PAGE_SIZE = 25;
